// 年度編輯 modal（config-annual「新增年度／修改年度」＝legacy `#dialogModal > #dialogForm`）。
//
// legacy 流程（行號逐檔 grep -n 親驗，2026-09-23；詳 docs/plans/2026-09-22-batch12-config-modal-progress.md §1.2）：
//   - showModal（annual.jsp:92-131）：
//       停用列（isEnable==0）→ btnSave hide＋year/sort disabled（:95-97；**radio 不鎖**，
//       :95-101 只動這兩欄）；啟用列 → 恢復（:99-101）。
//       新增 → year 空、isDefaultRadio0（否）checked、sort=0、title 新增年度（:104-107/:105）。
//       修改 → year=rowObj.year、sort=rowObj.sort（:110-111）；isDefault==1 → radio1 checked、
//         否則 radio0（:113-118）；title 修改年度（:122）。
//   - updateInfoDiv：annual.jsp:460-466 整段被 HTML 註解——:108 hide／:123 show 都是對空集合
//     的 no-op＝legacy-known-issue，雛形照實不畫（進度檔 §6.4）。
//   - save（:146-181）：rules year {required, digits}（:148-150）→ jquery.validate inline
//     `必填欄位`／`必須為整數`（decorator.jsp:262/:269；年度西元範圍 2000-9999 的
//     `請輸入西元年` 在雛形由 resource-rules 的欄位錯誤承接，annual.jsp:12-22）；
//     sort 空→'0'（:156）；serialize＋isDefault radio（:157）；POST config/saveAnnualAjax（:160）；
//     成功 → doQuery＋modal hide＋showInfoDialog(desc)（:163-166）；失敗 → doQuery＋
//     showAlertDialog(desc)（:168/:173）。
//
// 雛形資料策略：
//   - 存檔走既有 `dataSource.create/update('config-annual',…)`（D-1 五個 op 之內）；真呼叫
//     DataSource 的存檔鈕才帶 data-effect（C5-j）。
//   - legacy isDefault=1/0（radio value）↔ mock 列 status='是'/'否'（進度檔 D5）；存「是」時
//     其餘列一律改「否」＝預設年度全站唯一（resource-rules applyResourceWriteSideEffects，
//     annual.jsp setDefaultAnnualAjax 語意）。
//   - mock 列鍵：date＝年度（欄位連結 key）、score＝排序、status＝是否預設、status2＝啟停。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Radio } from '@/components/base/Radio';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { MockDataError, resolveLegacyMessage, useDataSource } from '@/mocks';
import type { Row } from '@/mocks/types';

export interface AnnualEditModalProps {
  readonly pageId: string;
  /** 'create'＝新增（showModal(0)）；'edit'＝修改（年度連結 .showEdit）。 */
  readonly mode: 'create' | 'edit';
  /** 修改時的列（新增時 null）。 */
  readonly row: Row | null;
  readonly onClose: () => void;
  readonly onClosed: () => void;
}

/** jquery.validate 全域訊息（decorator.jsp:262/:266）。 */
const REQUIRED_TEXT = '必填欄位';
const DIGITS_TEXT = '必須為整數';

export function AnnualEditModal({
  pageId,
  mode,
  row,
  onClose,
  onClosed,
}: AnnualEditModalProps): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  /** 取消／關閉：只關 modal。legacy annual.jsp 沒有 hidden.bs.modal → doQuery 的掛載
   * （doQuery 只在 ready/save 後，R2-F5）；reload 由存檔流程自己帶。 */
  const closeMainModal = () => {
    onClose();
  };
  /** 存檔後的清單刷新（legacy save() 成功/失敗都先 doQuery，annual.jsp:163/:168）。 */
  const reloadList = () => {
    onClosed();
  };

  const isEdit = mode === 'edit' && row !== null;
  // 停用列（annual.jsp:95-97）：btnSave hide＋year/sort disabled；radio 不在鎖定範圍。
  const locked = isEdit && String(row?.['status2'] ?? '') === '停用';

  // ------------------------------------------------------------------
  // 表單值（showModal :104-118）。isDefault 內部值域 '1'/'0'（radio value），存檔映射 是/否。
  // ------------------------------------------------------------------
  const [year, setYear] = useState(isEdit ? String(row?.['date'] ?? '') : '');
  const [sort, setSort] = useState(isEdit ? String(row?.['score'] ?? '') : '0'); // :107 sort=0
  const [isDefault, setIsDefault] = useState<'1' | '0'>(
    isEdit && String(row?.['status'] ?? '') === '是' ? '1' : '0', // :106/:113-118
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const title = isEdit ? '修改年度' : '新增年度'; // annual.jsp:122/:105

  const alertOrGeneric = async (cause: unknown) => {
    await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
  };

  const saveSuccessText = () =>
    resolveLegacyMessage(pageId, 'success')?.text ?? t('state.saveSuccess');

  // ------------------------------------------------------------------
  // 存檔（legacy save，annual.jsp:146-181）
  // ------------------------------------------------------------------
  const save = () => {
    void (async () => {
      // rules year {required, digits}（:148-150）→ inline `必填欄位`／`必須為整數`。
      const errors: Record<string, string> = {};
      if (year.trim() === '') {
        errors.year = REQUIRED_TEXT;
      } else if (!/^\d+$/.test(year.trim())) {
        errors.year = DIGITS_TEXT;
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      // :156（sort 空 → '0'）；payload 對照 mock 列鍵（date/score/status）。
      const payload: Row = {
        date: year.trim(),
        score: sort === '' ? '0' : sort,
        status: isDefault === '1' ? '是' : '否',
      };
      setBusy(true);
      try {
        if (isEdit) {
          await dialogs.processing.wrap(dataSource.update(pageId, String(row?.id ?? ''), payload));
        } else {
          await dialogs.processing.wrap(dataSource.create(pageId, payload));
        }
        // 成功：doQuery＋modal hide＋showInfoDialog(desc)（:163-166）。
        closeMainModal();
        reloadList();
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        // 失敗：doQuery＋showAlertDialog(desc)（:168/:173）；欄位級錯誤（請輸入西元年，
        // resource-rules 承接 annual.jsp:12-22 ADYear validator）inline 顯示在年度欄。
        // mock 列鍵（date/score）映射回表單欄位（year/sort）。
        reloadList();
        const fields = cause instanceof MockDataError ? { ...cause.fields } : {};
        const mapped: Record<string, string> = {};
        if (fields['date'] !== undefined) {
          mapped.year = fields['date'];
        }
        if (fields['score'] !== undefined) {
          mapped.sort = fields['score'];
        }
        if (Object.keys(mapped).length > 0) {
          setFieldErrors(mapped);
          return;
        }
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <ModalFrame
      uiId={`${pageId}-form-dialog`}
      open
      title={title}
      size="lg"
      onClose={closeMainModal}
      footer={
        <>
          <Button
            uiId={`${pageId}-form-dialog-cancel`}
            variant="default"
            size="sm"
            type="button"
            onClick={closeMainModal}
          >
            {/* annual.jsp:469（btn-close data-dismiss) */}
            關閉
          </Button>
          {/* :471（btnSave onclick=save()）；停用列 btnSave.hide（:95）。 */}
          {locked ? null : (
            <Button
              uiId={`${pageId}-form-dialog-save`}
              variant="primary"
              size="sm"
              type="button"
              disabled={busy}
              // C5-j：存檔真的呼叫 dataSource.create/update。
              dataEffect={`${isEdit ? 'update' : 'create'}:${pageId}`}
              onClick={save}
            >
              存檔
            </Button>
          )}
        </>
      }
    >
      <div data-ui-id={`${pageId}-form-dialog-body`} className="space-y-3">
        {/* 年度（annual.jsp:442-447，required＋西元 2000-9999；雛形以文字輸入呈現數字欄）。 */}
        <div className="flex items-start gap-2">
          <label className="shrink-0 text-sm" htmlFor={`${pageId}-form-dialog-input-year`}>
            <span className="text-danger">*</span>年度：
          </label>
          <div>
            <TextInput
              uiId={`${pageId}-form-dialog-input-year`}
              value={year}
              onChange={setYear}
              disabled={locked || busy}
              invalid={fieldErrors.year !== undefined}
            />
            {fieldErrors.year === undefined ? null : (
              <div data-ui-id={`${pageId}-form-dialog-error-year`} className="text-xs text-danger">
                {fieldErrors.year}
              </div>
            )}
          </div>
        </div>
        {/* 系統預設年度（:448-452）＝radio 是(1)/否(0)；停用列也不鎖（:95-101 只動 year/sort）。 */}
        <div className="flex items-start gap-2">
          <label className="shrink-0 text-sm">系統預設年度：</label>
          <div className="flex items-center gap-3">
            <Radio
              uiId={`${pageId}-form-dialog-input-is-default-radio1`}
              name="isDefault"
              checked={isDefault === '1'}
              onChange={(checked) => {
                if (checked) {
                  setIsDefault('1');
                }
              }}
              disabled={busy}
              label=" 是 "
            />
            <Radio
              uiId={`${pageId}-form-dialog-input-is-default-radio0`}
              name="isDefault"
              checked={isDefault === '0'}
              onChange={(checked) => {
                if (checked) {
                  setIsDefault('0');
                }
              }}
              disabled={busy}
              label=" 否 "
            />
          </div>
        </div>
        {/* 排序序號（:455-459，number 0-99999）。 */}
        <div className="flex items-start gap-2">
          <label className="shrink-0 text-sm" htmlFor={`${pageId}-form-dialog-input-sort`}>
            排序序號：
          </label>
          <TextInput
            uiId={`${pageId}-form-dialog-input-sort`}
            value={sort}
            onChange={setSort}
            disabled={locked || busy}
          />
        </div>
        {/* updateInfoDiv：legacy 已整段註解（:460-466），show/hide 為空集合 no-op——照實不畫。 */}
      </div>
    </ModalFrame>
  );
}
